import { Avatar, Typography } from "@norican/react";

import { BusinessCard } from "@/features/card/business-card";
import { CardActions } from "@/features/card/card-actions";
import type { CardProfile } from "@/lib/api";

interface CardViewProps {
  card: CardProfile;
  /** `/api/card/me/photo` or `/api/card/{id}/photo`. */
  photoUrl: string;
  /** Whose card this is — changes the copy around it, not the card. */
  owner: "me" | "colleague";
}

/** One card page: identity row, the flippable card, the actions, the provenance note. */
export function CardView({ card, photoUrl, owner }: CardViewProps) {
  const initials = [card.given_name?.[0], card.surname?.[0]].filter(Boolean).join("").toUpperCase();
  const subtitle = [card.job_title, card.department].filter(Boolean).join(" · ");

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center gap-4">
        <Avatar.Root size="xl">
          {card.has_photo && <Avatar.Image src={photoUrl} alt="" />}
          <Avatar.Fallback>{initials || "N"}</Avatar.Fallback>
        </Avatar.Root>
        <div className="min-w-0">
          <Typography as="h1" variant="heading-2" truncate>
            {card.display_name}
          </Typography>
          {subtitle && (
            <Typography variant="body-sm" tone="muted">
              {subtitle}
            </Typography>
          )}
        </div>
      </div>

      {/* The card column is capped at 560 px at every width, so the card is the same piece on a tablet, a half-screen window and a desktop. */}
      <div className="grid grid-cols-[minmax(0,560px)] items-start gap-8 lg:grid-cols-[minmax(0,560px)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <BusinessCard card={card} />
          <Typography variant="caption" tone="muted" className="text-center">
            Tap the card to turn it over.
          </Typography>
        </div>

        <div className="flex flex-col gap-6">
          <CardActions card={card} />
          <div className="bg-bg border-border rounded-lg border p-4">
            <Typography variant="label" className="mb-2">
              {owner === "me" ? "Where this comes from" : "About this card"}
            </Typography>
            <Typography variant="body-sm" tone="muted">
              {owner === "me"
                ? "Your card is generated live from your Microsoft Entra profile: name, job title, phone numbers and office address. If something is wrong, raise a ticket with the IT helpdesk to have your profile updated; the card follows automatically."
                : "This card is generated live from the colleague's Microsoft Entra profile. Scanning the QR code or saving the contact adds exactly these details to a phone."}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}
